import { IPreHeatRecipeEditable, IRecipeEditable, IRecipeStepEditable, StepMode } from '../../types/recipes-types';
import * as Yup from 'yup';
import React from 'react';
import { FormikConfig } from 'formik';
import i18n from '../../infrastructure/i18n';
import { POST_WELD_SETTINGS_LIMITS } from '../../components/PostWeldSettings/constants';
import { LATIN_ALPHANUMERIC_WITH_SPACES_REGEX } from '../../constants/common';
import CycleSettings from '../../enums/cycle-setting.enum';

export const initialValues: IRecipeEditable = {
  name: '',
  mode: CycleSettings.PostWeld,
  holdUpperTolerance: undefined,
  rateUpperTolerance: undefined,
  holdLowerTolerance: undefined,
  rateLowerTolerance: undefined,
};

export const initialStepValues: IRecipeStepEditable = {
  mode: StepMode.Rate,
  durationDays: 0,
  durationHours: 0,
  durationMinutes: 0,
  durationMinutesTotal: 0,
  heatingRate: 0,
  heatingLimit: 0,
  order: 0,
  isCelsius: true,
};

export const validationSchema: FormikConfig<IRecipeEditable>['validationSchema'] = Yup.object({
  name: Yup.string()
    .required(i18n.t('errors.requiredValue'))
    .min(
      POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MIN,
      i18n.t('errors.stringValue.min', '', {
        maxChars: POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MIN,
        value: i18n.t('cycle.settings.postWeld.recipeName'),
      }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MAX,
      i18n.t('errors.stringValue.max', '', {
        maxChars: POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MAX,
        value: i18n.t('cycle.settings.postWeld.recipeName'),
      }),
    )
    .matches(LATIN_ALPHANUMERIC_WITH_SPACES_REGEX, i18n.t('errors.stringValue.sanitizeAlphaNumericChars')),
  mode: Yup.string().required('Required'),
  holdUpperTolerance: Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MIN }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
  rateUpperTolerance: Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MIN }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
  holdLowerTolerance: Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MIN }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
  rateLowerTolerance: Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MIN }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
});

export const validationStepSchema: FormikConfig<IRecipeStepEditable>['validationSchema'] = Yup.object({
  mode: Yup.string().required('Required'),
  durationDays: Yup.number(),
  durationHours: Yup.number(),
  durationMinutes: Yup.number(),
  heatingRate: Yup.number(),
  heatingLimit: Yup.number(),
});

export const fields: {
  key: keyof IRecipeEditable;
  label: string;
  placeholder: string;
  inputType: React.InputHTMLAttributes<HTMLInputElement>['type'];
}[] = [
  {
    key: 'name',
    inputType: 'text',
    label: 'Name',
    placeholder: 'Enter new recipe name',
  },
  {
    key: 'rateUpperTolerance',
    inputType: 'number',
    label: 'Rate Upper Tolerance',
    placeholder: 'Enter rate upper tolerance',
  },
  {
    key: 'rateLowerTolerance',
    inputType: 'number',
    label: 'Rate Lower Tolerance',
    placeholder: 'Enter rate lower tolerance',
  },
  {
    key: 'holdUpperTolerance',
    inputType: 'number',
    label: 'Hold Upper Tolerance',
    placeholder: 'Enter hold upper tolerance',
  },
  {
    key: 'holdLowerTolerance',
    inputType: 'number',
    label: 'Hold Lower Tolerance',
    placeholder: 'Enter hold lower tolerance',
  },
];

export const preHeatRecipeFields: {
  key: keyof IPreHeatRecipeEditable;
  label: string;
  placeholder: string;
  inputType: React.InputHTMLAttributes<HTMLInputElement>['type'];
}[] = [
  {
    key: 'name',
    inputType: 'text',
    label: 'Name',
    placeholder: 'Enter new recipe name',
  },
  {
    key: 'rate',
    inputType: 'number',
    label: 'Rate',
    placeholder: 'Enter rate (rt)',
  },
  {
    key: 'finalTemp',
    inputType: 'number',
    label: 'Final Temperature',
    placeholder: 'Enter final temperature (ht)',
  },
  {
    key: 'soakUpper',
    inputType: 'number',
    label: 'Soak upper tolerance',
    placeholder: 'Enter soak upper tolerance (up)',
  },
  {
    key: 'soakLower',
    inputType: 'number',
    label: 'Soak lower tolerance',
    placeholder: 'Enter soak lower tolerance (lw)',
  },
];

export const RECIPE_PATHS = {
  BASE: '/recipes',
  CREATE: 'create',
  EDIT_BASE: ':id',
};

export const EDIT_RECIPE_PATHS = {
  EDIT_RECIPE: 'edit',
  STEPS: 'steps',
  CREATE_STEP: 'create',
  EDIT_STEP: ':step_id/edit',
};

export const editRecipeFullPath = `${RECIPE_PATHS.BASE}/${RECIPE_PATHS.EDIT_BASE}/${EDIT_RECIPE_PATHS.EDIT_RECIPE}`;

export const recipeStepsFullPath = `${RECIPE_PATHS.BASE}/${RECIPE_PATHS.EDIT_BASE}/${EDIT_RECIPE_PATHS.STEPS}`;
export const editRecipeStepFullPath = `${recipeStepsFullPath}/${EDIT_RECIPE_PATHS.EDIT_STEP}`;

export const undefinedIndex = -1;

export const stepsLimitCount = 10;
